import React, { useState } from "react";
import { useSnackbar } from "notistack";

import { OrganizationAggregate } from "../../../dtos/organizationAggregate";
import { ActiveStatus } from "../../../enums/ActiveStatus";
import { AjaxResult } from "../../../enums/ajaxResult";
import { useAuth } from "../../../features/auth/context";
import { useConfirmDialog } from "../../../hooks/useConfirmDialog";
import { checkIfUserCanActivate } from "../../../utils/activation";
import { useApi } from "../../useApi";
import { navLinkUrl } from "../../../utils";
import { RowActionsMenu, RowAction } from "../../shared/RowActionsMenu";

interface Props {
    org: OrganizationAggregate;
    dealerId: number | undefined;
    orgsAreLoading: boolean;
    onDeleteOrg: (id: number) => void;
    onLoadOrgs: () => void;
}

/**
 * Renders org-row actions in two layers:
 *
 *  - Inline links (Sites / Configure) stay top-level so the e2e Playwright
 *    suite can find them with the existing `a:has-text('Configure')` etc.
 *    selectors.
 *  - Destructive actions (Delete + Activate / Deactivate) are folded into a
 *    single `Actions ▾` overflow menu, both still gated by
 *    `useConfirmDialog`.
 */
export function OrgRowActions({ org, dealerId, orgsAreLoading, onDeleteOrg, onLoadOrgs }: Props) {
    const { orgApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const { showConfirmDialog } = useConfirmDialog();
    const auth = useAuth();
    const [isLoading, setIsLoading] = useState(false);

    const isUsersDealerDeactivated = auth.dealerId !== null && !auth.isDealerActive;
    const busy = isLoading || orgsAreLoading || isUsersDealerDeactivated;

    const isActive = org.activeStatus === ActiveStatus.Active;
    const userCanActivate = checkIfUserCanActivate(
        org.activeStatus,
        auth.isDealerAdmin,
        auth.isGlobalAdmin,
        auth.isOrgAdmin,
    );

    const deleteOrg = () => {
        showConfirmDialog(`Are you sure you want to delete "${org.name}" organization?`).then((confirmed) => {
            if (!confirmed) return;
            setIsLoading(true);
            orgApi.delete(org.id).then((r) => {
                if (r.result === AjaxResult.Success) {
                    enqueueSnackbar("Organization deleted!");
                    onDeleteOrg(org.id);
                } else {
                    enqueueSnackbar("Failed to delete organization", { variant: "error" });
                }
                setIsLoading(false);
            });
        });
    };

    const activateOrg = () => {
        showConfirmDialog(`Are you sure you want to activate organization "${org.name}"?`).then((confirmed) => {
            if (!confirmed) return;
            setIsLoading(true);
            orgApi.activate(org.id).then((r) => {
                if (r.result === AjaxResult.Success) {
                    onLoadOrgs();
                } else {
                    enqueueSnackbar(`Failed to activate organization "${org.name}"`, { variant: "error" });
                }
                setIsLoading(false);
            });
        });
    };

    const deactivateOrg = () => {
        showConfirmDialog(`Are you sure you want to deactivate organization "${org.name}"?`).then((confirmed) => {
            if (!confirmed) return;
            setIsLoading(true);
            orgApi.deactivate(org.id).then((r) => {
                if (r.result === AjaxResult.Success) {
                    onLoadOrgs();
                } else {
                    enqueueSnackbar(`Failed to deactivate organization "${org.name}"`, { variant: "error" });
                }
                setIsLoading(false);
            });
        });
    };

    const menuActions: RowAction[] = [
        {
            label: "Sites",
            to: `/dealers/${dealerId}/organizations/${org.id}/sites`,
        },
        {
            label: "Configure",
            to: navLinkUrl("organization-config", dealerId, org.id),
        },
        {
            label: isActive ? "Deactivate" : "Activate",
            onClick: isActive ? deactivateOrg : activateOrg,
            dangerous: isActive,
            disabled: busy || (!isActive && !userCanActivate),
            dividerBefore: true,
        },
        {
            label: "Delete",
            onClick: deleteOrg,
            dangerous: true,
            disabled: busy,
        },
    ];

    return <RowActionsMenu actions={menuActions} ariaLabel={`Actions for ${org.name}`} />;
}
